import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, roleHome } from "../lib/auth";
import { warmupBackend } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";

export default function LoginPage() {
  const { loginStaff } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [staff, setStaff] = React.useState({ email: "", password: "" });
  const [busy, setBusy] = React.useState(false);

  // Warm the backend up as soon as the login page mounts. After idle the pod
  // takes ~5–10 s to boot; without this, the user's first click would fail.
  React.useEffect(() => {
    warmupBackend();
  }, []);

  const onStaffLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await loginStaff(staff.email, staff.password);
      toast.success(`Welcome, ${u.name || u.email}`);
      nav(loc.state?.from || roleHome(u.role));
    } catch (err) {
      // Distinguish "wrong password" (server responded with detail) vs
      // "cold-start/network blip" (no response object).
      let msg;
      if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      } else if (!err.response) {
        msg = "Server is waking up. Please wait a few seconds and try again.";
      } else {
        msg = `Login failed (HTTP ${err.response.status})`;
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portfolio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-screen flex">
      {/* Hero */}
      <div
        className="hidden lg:flex flex-1 relative bg-zinc-950 items-end p-12"
        style={{
          backgroundImage:
            "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.15) 100%), url('https://images.unsplash.com/photo-1767658647133-2168a9cfc1d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBnbGFzcyUyMGFyY2hpdGVjdHVyZSUyMGJ1aWxkaW5nJTIwd2hpdGUlMjBncmV5JTIwYWJzdHJhY3R8ZW58MHx8fHwxNzc3OTc2MDU0fDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="text-white max-w-xl">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 mb-4">
            AlphaCompounding · Portfolio Advisory
          </div>
          <h1 className="font-display font-bold text-5xl leading-[1.05] mb-4">
            Equity portfolios, engineered for clarity.
          </h1>
          <p className="text-zinc-200 text-base max-w-md">
            Onboard clients, run recommendations, and deliver institutional-grade portfolio performance — all from one console.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center bg-white px-6 py-10">
        <Card className="w-full max-w-md border-zinc-200 shadow-none">
          <CardContent className="p-8">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-6">
                <span className="h-8 w-8 rounded-md bg-zinc-950 text-white grid place-items-center font-display font-bold">
                  P
                </span>
                <div>
                  <div className="font-display font-bold text-zinc-950 text-lg">AlphaCompounding</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                    Portfolio Advisory
                  </div>
                </div>
              </div>
              <h2 className="font-display font-semibold text-2xl text-zinc-950">
                Sign in to your console
              </h2>
              <p className="text-sm text-zinc-500 mt-1">
                One sign-in for everyone. We&apos;ll route you based on your account.
              </p>
            </div>

            <form onSubmit={onStaffLogin} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-zinc-700">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  placeholder="you@alphacompounding.in"
                  value={staff.email}
                  onChange={(e) => setStaff({ ...staff, email: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-zinc-700">Password</Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={staff.password}
                  onChange={(e) => setStaff({ ...staff, password: e.target.value })}
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                data-testid="login-submit"
                disabled={busy}
                className="w-full bg-zinc-950 hover:bg-zinc-800 text-white"
              >
                {busy ? "Signing in..." : "Sign in"}
              </Button>

              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-zinc-200" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-zinc-500">or</span></div>
              </div>
              <Button
                data-testid="google-login-btn"
                type="button"
                onClick={onGoogle}
                className="w-full bg-white border border-zinc-200 text-zinc-900 hover:bg-zinc-50"
              >
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M21.35 11.1H12v3.2h5.32c-.23 1.4-1.71 4.1-5.32 4.1-3.2 0-5.81-2.65-5.81-5.91s2.61-5.91 5.81-5.91c1.82 0 3.04.78 3.74 1.45l2.55-2.46C16.6 4.04 14.5 3 12 3 6.99 3 3 6.99 3 12s3.99 9 9 9c5.2 0 8.65-3.65 8.65-8.79 0-.59-.07-1.05-.17-1.51z"/></svg>
                Sign in with Google
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
